import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PIPELINE_STAGES } from "@/lib/constants";
import { type PipelineStage } from "@/lib/types";

interface ActivityItem {
  id: string;
  company: string;
  contactName: string;
  stage: PipelineStage;
  updatedAt: number;
}

interface RecentActivityProps {
  recentActivity: ActivityItem[];
}

export function RecentActivity({ recentActivity }: RecentActivityProps) {
  const formatTimeAgo = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) {
      return `${minutes}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else if (days < 7) {
      return `${days}d ago`;
    } else {
      return new Date(timestamp).toLocaleDateString();
    }
  };

  const getStageLabel = (stage: PipelineStage) => {
    return PIPELINE_STAGES.find(s => s.value === stage)?.label || stage;
  };

  const getStageColor = (stage: PipelineStage) => {
    switch (stage) {
      case 'new':
        return 'bg-slate-100 text-slate-800';
      case 'contacted':
        return 'bg-blue-100 text-blue-800';
      case 'qualified':
        return 'bg-yellow-100 text-yellow-800';
      case 'interested':
        return 'bg-orange-100 text-orange-800';
      case 'committed':
        return 'bg-purple-100 text-purple-800';
      case 'participant':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };

  if (recentActivity.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-muted-foreground">No recent activity</p>
            <p className="text-sm text-muted-foreground mt-1">
              Prospect updates will appear here
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent Activity</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {recentActivity.map((item) => (
            <div key={item.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-b-0">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-medium text-sm truncate">{item.company}</p>
                  <Badge 
                    variant="outline" 
                    className={`text-xs ${getStageColor(item.stage)}`}
                  >
                    {getStageLabel(item.stage)}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  {item.contactName}
                </p>
              </div>
              <div className="text-xs text-muted-foreground ml-2 flex-shrink-0">
                {formatTimeAgo(item.updatedAt)}
              </div>
            </div>
          ))}
        </div>
        
        {recentActivity.length === 10 && (
          <div className="text-center pt-4 border-t">
            <p className="text-xs text-muted-foreground">
              Showing last 10 activities
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}